import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UserIcon {
  id: string;
  user_name: string;
  icon_name: string;
}

export function useUserIcons() {
  const queryClient = useQueryClient();

  const { data: userIcons = [], isLoading } = useQuery({
    queryKey: ['user-icons'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_icons')
        .select('*');
      
      if (error) {
        console.error('Error fetching user icons:', error);
        return [];
      }
      
      return data as UserIcon[];
    },
  });

  const setIconMutation = useMutation({
    mutationFn: async ({ userName, iconName }: { userName: string; iconName: string }) => {
      // Try to update first, then insert if not exists
      const { data: existing } = await supabase
        .from('user_icons')
        .select('id')
        .eq('user_name', userName)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('user_icons')
          .update({ icon_name: iconName })
          .eq('user_name', userName);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_icons')
          .insert({ user_name: userName, icon_name: iconName });
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-icons'] });
    },
  });

  const getUserIcon = (userName: string): string | null => {
    const icon = userIcons.find(i => i.user_name === userName);
    return icon?.icon_name || null;
  };

  const setUserIcon = async (userName: string, iconName: string) => {
    await setIconMutation.mutateAsync({ userName, iconName });
  };

  return {
    userIcons,
    isLoading,
    getUserIcon,
    setUserIcon,
    isSaving: setIconMutation.isPending,
  };
}
